/**
 * Базовый движок раскроя — MaxRects, 1 проход (CUT-4.1).
 *
 * Детерминированный, чистый TypeScript: одинаковый вход → байт-в-байт одинаковый
 * `Layout` (без `Date`/`Math.random`). Multi-pass (9 проходов) + полная
 * 5-критериальная оптимизация + раскрой на 2 размерах листа (§5.1.9) — CUT-4.2.
 *
 * @see ADR-0074, TRD §5.1, §5.1.1–§5.1.9
 */

import type { CuttingSettings, Part, SheetSizeConfig } from '@pomoshnik/shared';
import type {
  EngineInput,
  EngineMetrics,
  FreeRect,
  Layout,
  OffcutSpec,
  PartInstance,
  Placement,
  PlacementWarning,
  SheetLayout,
  SheetSpec,
} from './types';
import { computeAllowedOrientations, mergeFreeRects, splitGuillotine } from './geometry';
import {
  findBestRect,
  PLACEMENT_HEURISTICS,
  type PlacementChoice,
  type PlacementHeuristic,
} from './place';
import { computeKim, multiCriteriaScore } from './metrics';

const EPS = 1e-6;

/**
 * Отступы полезной зоны листа (§5.3). `base` — единый `edgeOffset`; per-side
 * (`top/bottom/left/right`) переопределяют его по соответствующей стороне, если
 * заданы. Система координат: `x` вдоль длины (`left`→x-min, `right`→x-max),
 * `y` вдоль ширины (`bottom`→y-min, `top`→y-max).
 */
interface UsableOffsets {
  base: number;
  top?: number | undefined;
  bottom?: number | undefined;
  left?: number | undefined;
  right?: number | undefined;
}

/**
 * Доменные правила размещения §5.3 (CUT-4.4a). Каждое опционально: `undefined` →
 * правило выключено (поведение CUT-4.2, back-compat).
 */
interface DomainRules {
  /** Текстура §5.3 (вариант A): см. `computeAllowedOrientations`. */
  considerTexture?: boolean | undefined;
  /** Ёмкость параллельного упора (мм): габарит вдоль ширины (`partH`, ось y). */
  uporLimit?: number | undefined;
  /** Максимальный прогон каретки (мм): cap по `max(длина, ширина)`. */
  maxCarriageRun?: number | undefined;
  /** Минимальная ширина полосы (мм): тонкие остатки при split → отход. */
  minStripWidth?: number | undefined;
  /**
   * §5.1.8 (CUT-4.4c): бонус к score за «длинный» остаток (remW ≥ 600 мм). Жертвует
   * 1–3% КИМ ради крупных пригодных остатков. `false` → чистая эвристика.
   */
  preferLongOffcuts: boolean;
  /** §5.1.8 (CUT-4.4c): бонус к score за «широкий» остаток (remH ≥ 400 мм). */
  preferWideOffcuts: boolean;
}

/**
 * Контекст приоритетной укладки в остатки (§5.1.8, CUT-4.4b). `enabled` —
 * `settings.useOffcutsFirst === true`. `specs` — развёрнутые по qty и
 * отсортированные по площади ВОЗРАСТАНИЯ экземпляры остатков (мельче сначала,
 * §5.3). `edgeOffset` — обпил остатка (`settings.offcutEdgeOffset ?? 0`, П.3=0).
 */
interface OffcutContext {
  enabled: boolean;
  specs: readonly OffcutSpec[];
  edgeOffset: number;
}

/** Компаратор экземпляров для сортировки прохода (стабильный, детерминированный). */
export type InstanceComparator = (a: PartInstance, b: PartInstance) => number;

/** Лексикографический tie-break по `instanceId` (детерминизм при равенстве ключа). */
function byInstanceId(a: PartInstance, b: PartInstance): number {
  return a.instanceId < b.instanceId ? -1 : a.instanceId > b.instanceId ? 1 : 0;
}

/** area-desc → longest-side-desc → instanceId (полностью детерминированный порядок). */
function compareByAreaDesc(a: PartInstance, b: PartInstance): number {
  if (b.area !== a.area) return b.area - a.area;
  const longA = Math.max(a.length, a.width);
  const longB = Math.max(b.length, b.width);
  if (longB !== longA) return longB - longA;
  return byInstanceId(a, b);
}

/** max-side-desc → area-desc → instanceId (длинные детали первыми). */
function compareByMaxSideDesc(a: PartInstance, b: PartInstance): number {
  const longA = Math.max(a.length, a.width);
  const longB = Math.max(b.length, b.width);
  if (longB !== longA) return longB - longA;
  if (b.area !== a.area) return b.area - a.area;
  return byInstanceId(a, b);
}

/** width-desc → length-desc → instanceId (§5.1: группирует детали равной ширины в ряды). */
function compareByWidthDesc(a: PartInstance, b: PartInstance): number {
  if (b.width !== a.width) return b.width - a.width;
  if (b.length !== a.length) return b.length - a.length;
  return byInstanceId(a, b);
}

/** 3 sort-стратегии multi-pass (§5.1, CUT-4.2) в фиксированном порядке. */
export const SORT_COMPARATORS: readonly InstanceComparator[] = [
  compareByAreaDesc,
  compareByMaxSideDesc,
  compareByWidthDesc,
];

/**
 * Раскрытие деталей по `qty` (§5.1 `expandParts`). Сортировка задаётся
 * компаратором (по умолчанию area-desc — поведение CUT-4.1).
 */
export function expandParts(
  parts: readonly Part[],
  comparator: InstanceComparator = compareByAreaDesc,
): PartInstance[] {
  const instances: PartInstance[] = [];
  for (const part of parts) {
    for (let k = 0; k < part.qty; k++) {
      instances.push({
        instanceId: `${part.id}#${k}`,
        partId: part.id,
        length: part.length,
        width: part.width,
        rotation: part.rotation,
        material: part.material ?? '',
        area: part.length * part.width,
      });
    }
  }
  instances.sort(comparator);
  return instances;
}

/**
 * Раскрытие остатков по `qty` + сортировка по площади ВОЗРАСТАНИЯ (§5.3: мельче
 * сначала — мелкие обрезки расходуются первыми). Tie-break — `offcutId` (детерминизм).
 * Каждый экземпляр получает уникальный `offcutId` вида `${id}#${k}`.
 */
export function expandOffcuts(offcuts: readonly OffcutSpec[]): OffcutSpec[] {
  const out: OffcutSpec[] = [];
  for (const o of offcuts) {
    for (let k = 0; k < o.qty; k++) {
      out.push({
        offcutId: `${o.offcutId}#${k}`,
        length: o.length,
        width: o.width,
        qty: 1,
        material: o.material,
      });
    }
  }
  out.sort((a, b) => {
    const areaA = a.length * a.width;
    const areaB = b.length * b.width;
    if (areaA !== areaB) return areaA - areaB; // площадь возрастания (мельче первым)
    return a.offcutId < b.offcutId ? -1 : a.offcutId > b.offcutId ? 1 : 0;
  });
  return out;
}

/**
 * Выбор листа. CUT-4.1: `uniform` — заданный размер; `mixed` — больший по площади
 * из двух вариантов (оптимальная комбинация количеств = CUT-4.2, §5.1.9).
 */
export function resolveSheetSpec(config: SheetSizeConfig): SheetSpec {
  if (config.mode === 'uniform') {
    if (!config.uniformSheet) {
      throw new Error('cutting-engine: uniform sheetConfig без uniformSheet');
    }
    return { length: config.uniformSheet.length, width: config.uniformSheet.width };
  }
  if (!config.mixedVariants) {
    throw new Error('cutting-engine: mixed sheetConfig без mixedVariants');
  }
  const [a, b] = config.mixedVariants;
  return a.length * a.width >= b.length * b.width
    ? { length: a.length, width: a.width }
    : { length: b.length, width: b.width };
}

/**
 * Полезная зона листа после обпила (§5.3). Per-side отступы переопределяют
 * единый `base`, если заданы; иначе — `base` со всех сторон (back-compat).
 */
function usableRect(spec: SheetSpec, off: UsableOffsets): FreeRect {
  const left = off.left ?? off.base;
  const right = off.right ?? off.base;
  const bottom = off.bottom ?? off.base;
  const top = off.top ?? off.base;
  return {
    x: left,
    y: bottom,
    w: spec.length - left - right,
    h: spec.width - bottom - top,
  };
}

/** Открыть новый лист с единственным свободным прямоугольником = полезная зона. */
function openLayout(spec: SheetSpec, off: UsableOffsets, index: number): SheetLayout {
  return {
    sheetId: `sheet-${index + 1}`,
    sheetIndex: index,
    sheetLength: spec.length,
    sheetWidth: spec.width,
    placements: [],
    freeRects: [usableRect(spec, off)],
  };
}

/**
 * Открыть лист-остаток (§5.1.8, CUT-4.4b): полезная зона остатка по обпилу
 * `offcutEdgeOffset` (П.3 — дефолт 0). Помечен `isOffcut`/`offcutId`/`material`
 * (только остаток-листы несут эти поля). `sheetIndex` — 0-based в пределах остатков.
 */
function openOffcutLayout(spec: OffcutSpec, edgeOffset: number, index: number): SheetLayout {
  return {
    sheetId: `offcut-${index + 1}`,
    sheetIndex: index,
    sheetLength: spec.length,
    sheetWidth: spec.width,
    placements: [],
    freeRects: [usableRect({ length: spec.length, width: spec.width }, { base: edgeOffset })],
    isOffcut: true,
    offcutId: spec.offcutId,
    material: spec.material,
  };
}

/** Отступы полезной зоны из настроек: единый `edgeOffset` + опц. per-side (§5.3). */
function resolveOffsets(settings: CuttingSettings): UsableOffsets {
  return {
    base: settings.edgeOffset,
    top: settings.edgeOffsetTop,
    bottom: settings.edgeOffsetBottom,
    left: settings.edgeOffsetLeft,
    right: settings.edgeOffsetRight,
  };
}

/** Доменные правила размещения из настроек (§5.3, CUT-4.4a; §5.1.8 prefer — CUT-4.4c). */
function resolveDomainRules(settings: CuttingSettings): DomainRules {
  return {
    considerTexture: settings.considerTexture,
    uporLimit: settings.uporLimit,
    maxCarriageRun: settings.maxCarriageRun,
    minStripWidth: settings.minStripWidth,
    // gated по === true: undefined/false → бонуса нет (back-compat CUT-4.2/4.4b).
    preferLongOffcuts: settings.preferLongOffcuts === true,
    preferWideOffcuts: settings.preferWideOffcuts === true,
  };
}

/** Учёт резов одной размещённой детали (прокси по split-операциям, см. EngineMetrics). */
interface CutTally {
  cuts: number;
  lengthMm: number;
}

/**
 * Применить выбранную позицию к листу: добавить `Placement`, разрезать
 * использованный freeRect (с резервом kerf), удалить вложенные куски.
 */
function applyPlacement(
  layout: SheetLayout,
  inst: PartInstance,
  choice: PlacementChoice,
  kerf: number,
  minStripWidth: number | undefined,
): CutTally {
  const { rect, rectIndex, rotated, partW, partH } = choice;

  const placement: Placement = {
    partId: inst.instanceId,
    x: rect.x,
    y: rect.y,
    w: partW,
    h: partH,
    rotated,
  };
  layout.placements.push(placement);

  // Гильотинный split: остаток справа/снизу за вычетом зазора реза (kerf).
  const usedW = partW + kerf;
  const usedH = partH + kerf;
  const children = splitGuillotine(rect, usedW, usedH);

  layout.freeRects.splice(rectIndex, 1);
  layout.freeRects.push(...children);
  // Слияние смежных коллинеарных кусков восстанавливает раздробленное гильотинным
  // split пространство (mergeFreeRects сам прогоняет pruneFreeRects).
  layout.freeRects = mergeFreeRects(layout.freeRects);
  // minStripWidth (§5.3): полосы тоньше N мм непригодны — после слияния отбрасываем
  // куски с короткой стороной < minStripWidth (уходят в отход, не под размещение).
  // Фильтр после merge: коллинеарные куски могут сложиться в годную полосу.
  if (minStripWidth !== undefined) {
    layout.freeRects = layout.freeRects.filter((r) => Math.min(r.w, r.h) >= minStripWidth - EPS);
  }

  const tally: CutTally = { cuts: 0, lengthMm: 0 };
  if (rect.w - usedW > EPS) {
    tally.cuts += 1;
    tally.lengthMm += partH; // вертикальный рез вдоль ширины детали
  }
  if (rect.h - usedH > EPS) {
    tally.cuts += 1;
    tally.lengthMm += partW; // горизонтальный рез вдоль длины детали
  }
  return tally;
}

/** Результат одного прохода раскроя (до подсчёта метрик). */
interface PassResult {
  /** Целые листы (свежий материал). Только они формируют знаменатель КИМ (П.6=ii). */
  layouts: SheetLayout[];
  /** Использованные (непустые) листы-остатки (§5.1.8). Вне знаменателя КИМ. */
  offcutLayouts: SheetLayout[];
  unplaced: string[];
  warnings: PlacementWarning[];
  /** Материалы деталей НА ЦЕЛЫХ ЛИСТАХ (пакеты свежего материала). Остатки не считаются. */
  materials: Set<string>;
  cutCount: number;
  cutLengthMm: number;
}

/**
 * Один проход раскроя: уже отсортированные `instances` раскладываются по листам
 * заданной `heuristic` выбора позиции. Каждая деталь размещается на первом
 * открытом листе с подходящим freeRect; иначе открывается новый лист из пула
 * (cap `nMax`). Деталь, не влезающая даже на свежий лист, → `unplaced`.
 *
 * Доменные правила §5.3 (CUT-4.4a) применяются ДО размещения экземпляра:
 *   1. `maxCarriageRun` — `max(длина, ширина)` сверх лимита → `unplaced` + warning
 *      (орт-независимо: повернуть нельзя, габарит не меняется).
 *   2. Текстура — фильтрует допустимые ориентации (`computeAllowedOrientations`).
 *   3. `uporLimit` — отбрасывает ориентации с `partH` (ось y) > лимита; если не
 *      осталось ни одной → `unplaced` + warning (орт-чувствительно: поворот спасает).
 *
 * Обрезки-первыми §5.1.8 (CUT-4.4b): при `offcutCtx.enabled` экземпляр сначала
 * пробуют разместить на ОСТАТКАХ (по площади возрастания, только при точном
 * совпадении материала — verbatim, P-001), и лишь затем — на целых листах.
 * Доменные правила §5.3 (maxCarriageRun/текстура/upor/minStrip) применяются и к
 * остаткам (общий код до развилки). Детали на остатках вне знаменателя КИМ (П.6=ii).
 */
function runPass(
  instances: readonly PartInstance[],
  spec: SheetSpec,
  off: UsableOffsets,
  kerf: number,
  nMax: number,
  heuristic: PlacementHeuristic,
  rules: DomainRules,
  offcutCtx: OffcutContext,
): PassResult {
  const layouts: SheetLayout[] = [];
  // Свежие листы-остатки на КАЖДЫЙ проход (без общей мутации между проходами).
  const offcutLayouts: SheetLayout[] = offcutCtx.enabled
    ? offcutCtx.specs.map((s, i) => openOffcutLayout(s, offcutCtx.edgeOffset, i))
    : [];
  const unplaced: string[] = [];
  const warnings: PlacementWarning[] = [];
  const materials = new Set<string>();
  let cutCount = 0;
  let cutLengthMm = 0;

  for (const inst of instances) {
    // §5.3 maxCarriageRun: деталь длиннее максимального прогона каретки физически
    // не вырезать ни в одной ориентации (cap по большему габариту) → не размещаем.
    if (
      rules.maxCarriageRun !== undefined &&
      Math.max(inst.length, inst.width) > rules.maxCarriageRun + EPS
    ) {
      unplaced.push(inst.instanceId);
      warnings.push({ instanceId: inst.instanceId, reason: 'maxCarriageRun' });
      continue;
    }

    let orientations = computeAllowedOrientations(inst.rotation, rules.considerTexture);

    // §5.3 uporLimit: упор ограничивает габарит детали вдоль ширины (partH, ось y).
    // Орт-чувствительно — оставляем ориентации, где partH ≤ лимита; если ни одной,
    // деталь не позиционируется упором ни в одной ориентации → не размещаем.
    if (rules.uporLimit !== undefined) {
      const limit = rules.uporLimit;
      orientations = orientations.filter((o) => {
        const partH = o === 'normal' ? inst.width : inst.length;
        return partH <= limit + EPS;
      });
      if (orientations.length === 0) {
        unplaced.push(inst.instanceId);
        warnings.push({ instanceId: inst.instanceId, reason: 'uporLimit' });
        continue;
      }
    }

    // §5.1.8 обрезки-первыми: пробуем остатки ДО целых листов. Остатки идут по
    // площади возрастания (мельче первым) — берём ПЕРВЫЙ остаток того же материала
    // (verbatim, P-001), на котором деталь влезает; внутри остатка позиция — по
    // текущей эвристике. Детали на остатках вне знаменателя КИМ (materials не
    // пополняем — остаток не пакет свежего материала, П.6=ii).
    if (offcutCtx.enabled) {
      let placedOnOffcut = false;
      for (const ol of offcutLayouts) {
        if (ol.material !== inst.material) continue; // материал-матч строго verbatim
        const choice = findBestRect(
          ol.freeRects,
          inst.length,
          inst.width,
          orientations,
          heuristic,
          rules.preferLongOffcuts,
          rules.preferWideOffcuts,
        );
        if (choice) {
          const tally = applyPlacement(ol, inst, choice, kerf, rules.minStripWidth);
          cutCount += tally.cuts;
          cutLengthMm += tally.lengthMm;
          placedOnOffcut = true;
          break;
        }
      }
      if (placedOnOffcut) continue;
    }

    // 1) Глобальный best-fit по ВСЕМ открытым листам: меньший score эвристики
    //    выигрывает (плотнее, чем first-fit). Tie-break — меньший индекс листа.
    let bestLayout: SheetLayout | null = null;
    let bestChoice: PlacementChoice | null = null;
    let bestScore = Number.POSITIVE_INFINITY;
    for (const layout of layouts) {
      const choice = findBestRect(
        layout.freeRects,
        inst.length,
        inst.width,
        orientations,
        heuristic,
        rules.preferLongOffcuts,
        rules.preferWideOffcuts,
      );
      if (choice && choice.score < bestScore - 1e-9) {
        bestScore = choice.score;
        bestLayout = layout;
        bestChoice = choice;
      }
    }

    // 2) Новый лист — только если не влез ни в один открытый (в пределах cap).
    if (!bestChoice && layouts.length < nMax) {
      const fresh = openLayout(spec, off, layouts.length);
      const choice = findBestRect(
        fresh.freeRects,
        inst.length,
        inst.width,
        orientations,
        heuristic,
        rules.preferLongOffcuts,
        rules.preferWideOffcuts,
      );
      if (choice) {
        layouts.push(fresh);
        bestLayout = fresh;
        bestChoice = choice;
      }
    }

    if (bestLayout && bestChoice) {
      const tally = applyPlacement(bestLayout, inst, bestChoice, kerf, rules.minStripWidth);
      cutCount += tally.cuts;
      cutLengthMm += tally.lengthMm;
      materials.add(inst.material);
    } else {
      unplaced.push(inst.instanceId);
    }
  }

  return {
    layouts,
    // Только непустые остаток-листы (пустые не загрязняют вывод/КИМ/схемы).
    offcutLayouts: offcutLayouts.filter((l) => l.placements.length > 0),
    unplaced,
    warnings,
    materials,
    cutCount,
    cutLengthMm,
  };
}

/**
 * Число уникальных раскладок (§5.1.7 `schemeCount`): листы с идентичной геометрией
 * размещений считаются одной схемой (на цехе режутся по одной карте). Сигнатура —
 * отсортированный список `(x,y,w,h,rotated)` всех размещений листа.
 */
function countUniqueSchemes(layouts: readonly SheetLayout[]): number {
  const seen = new Set<string>();
  for (const layout of layouts) {
    const sig = layout.placements
      .map((p) => `${p.x},${p.y},${p.w},${p.h},${p.rotated ? 1 : 0}`)
      .sort()
      .join('|');
    seen.add(sig);
  }
  return seen.size;
}

/** Собрать итоговый `Layout` (с метриками) из результата прохода. */
function buildLayout(pass: PassResult, spec: SheetSpec): Layout {
  const { layouts, offcutLayouts, unplaced, warnings, materials, cutCount, cutLengthMm } = pass;
  const sheetGrossArea = spec.length * spec.width;
  // КИМ целых листов (П.6=ii): и числитель, и знаменатель — ТОЛЬКО целые листы.
  // Детали на остатках («бесплатный» уже оплаченный материал) исключены из обеих
  // частей. Вырожденный случай all-on-offcuts → 0 целых листов → kim=0, waste=0
  // (EP-5 отобразит как «0 новых листов»).
  const usedSheetsArea = layouts.length * sheetGrossArea;
  const placedPartsArea = layouts.reduce(
    (sum, l) => sum + l.placements.reduce((s, p) => s + p.w * p.h, 0),
    0,
  );
  const kim = computeKim(placedPartsArea, usedSheetsArea);

  // Вывод: целые листы, затем использованные остаток-листы (EP-5 различает по isOffcut).
  const sheets = [...layouts, ...offcutLayouts];

  const metrics: EngineMetrics = {
    kim,
    wastePercent: usedSheetsArea > 0 ? (1 - kim) * 100 : 0,
    schemeCount: countUniqueSchemes(sheets),
    cutLengthMeters: cutLengthMm / 1000,
    cutCount,
    packageCount: materials.size,
  };

  return { sheets, unplaced, warnings, metrics };
}

/**
 * Все 9 кандидатов multi-pass = 3 sort-стратегии (`SORT_COMPARATORS`) × 3
 * placement-эвристики (`PLACEMENT_HEURISTICS`), в фиксированном порядке
 * (sort внешний цикл, heuristic внутренний). Порядок детерминирован — индекс
 * кандидата = `sortIdx * 3 + heuristicIdx`. При вырожденном входе (нет полезной
 * зоны) возвращает один кандидат со всеми деталями в `unplaced`.
 *
 * @see TRD §5.1, §5.1.7
 */
export function runAllPasses(input: EngineInput): Layout[] {
  const { parts, settings } = input;
  const kerf = settings.kerf;
  const off = resolveOffsets(settings);
  const rules = resolveDomainRules(settings);
  const spec = resolveSheetSpec(settings.sheetConfig);
  const usable = usableRect(spec, off);

  // Контекст обрезок-первыми (§5.1.8, CUT-4.4b): развёрнуты по qty + сорт площади ↑.
  const offcutCtx: OffcutContext = {
    enabled: settings.useOffcutsFirst === true,
    specs: expandOffcuts(input.offcuts ?? []),
    edgeOffset: settings.offcutEdgeOffset ?? 0, // П.3 — дефолт 0
  };
  const hasOffcuts = offcutCtx.enabled && offcutCtx.specs.length > 0;

  // Нет полезной зоны целого листа (обпил «съел» лист). БЕЗ остатков → все детали
  // неразмещаемы. С остатками (обрезки-первыми) — детали ещё могут лечь в остатки,
  // целые листы при этом не открываются (nMax = 0); не делаем ранний выход.
  if ((usable.w <= 0 || usable.h <= 0) && !hasOffcuts) {
    return [
      {
        sheets: [],
        unplaced: expandParts(parts).map((p) => p.instanceId),
        warnings: [],
        metrics: emptyMetrics(),
      },
    ];
  }

  const usableArea = usable.w * usable.h;
  // totalArea не зависит от сортировки — считаем один раз для cap пула листов.
  const totalArea = parts.reduce((sum, p) => sum + p.length * p.width * p.qty, 0);
  // Cap пула ЦЕЛЫХ листов по площади (§5.1.9). Теоретический минимум листов —
  // ceil(totalArea / usableArea); +1 как в §5.1.9, +ещё 1 — запас на неплотность
  // прохода (деталь нередко не влезает в теоретический минимум; запас лишь
  // расширяет потолок, ложных unplaced не создаёт). SLA держит ограничение O(N²).
  // Вырожденная зона (usableArea ≤ 0, достижимо лишь при hasOffcuts) → 0 целых листов.
  const nMax = usableArea > 0 ? Math.ceil(totalArea / usableArea) + 2 : 0;

  const candidates: Layout[] = [];
  for (const comparator of SORT_COMPARATORS) {
    const instances = expandParts(parts, comparator);
    for (const heuristic of PLACEMENT_HEURISTICS) {
      candidates.push(
        buildLayout(runPass(instances, spec, off, kerf, nMax, heuristic, rules, offcutCtx), spec),
      );
    }
  }
  return candidates;
}

/**
 * Раскрой деталей по листам — MaxRects multi-pass (CUT-4.2).
 *
 * Считает `multiCriteriaScore` (§5.1.7, меньше — лучше) каждого из 9 кандидатов
 * (`runAllPasses`) и возвращает кандидата с минимальным score. Детерминирован:
 * при равном score выигрывает меньший индекс прохода (порядок sort×heuristic
 * фиксирован) — без `Date`/`Math.random`.
 *
 * @see ADR-0074 D2, TRD §5.1, §5.1.7
 */
export function optimize(input: EngineInput): Layout {
  const candidates = runAllPasses(input);

  let best = candidates[0]!;
  let bestScore = multiCriteriaScore(best.metrics);
  for (let i = 1; i < candidates.length; i++) {
    const score = multiCriteriaScore(candidates[i]!.metrics);
    if (score < bestScore - 1e-9) {
      bestScore = score;
      best = candidates[i]!;
    }
  }
  return best;
}

function emptyMetrics(): EngineMetrics {
  return {
    kim: 0,
    wastePercent: 0,
    schemeCount: 0,
    cutLengthMeters: 0,
    cutCount: 0,
    packageCount: 0,
  };
}
